import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Divider,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/styles";
import React, { useContext, useEffect, useReducer, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AppContext from "../../components/context/context";
import DashboardContainer from "../../components/dashboard-container";
import ProductUploadModal from "../../components/file-upload-modal";
import ProductList from "../../components/product-list";
import UploadStatusChartDialog from "../../components/upload-status";
import CustomTypography from "../../globalStyles/fonts";
import ImportIcon from "../../globalStyles/icons/importIcon";
import { isMobile } from "../../globalStyles/mobile";
import { productFilterInitialState } from "../../utils/filter-config";
import buildQuery from "../../utils/query-builder";
import { productService } from "../../utils/services/productService";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import CategoryIcon from "@mui/icons-material/CategoryOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

function filterReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        columns: state?.columns.map((x) =>
          x?.key === action?.payload?.key ? { ...x, ...action.payload } : x,
        ),
      };
    case "SET_OPTIONS":
      return {
        ...state,
        columns: state.columns.map((col) =>
          col.key === action.payload.key
            ? { ...col, options: action.payload.options }
            : col,
        ),
      };
    case "UPDATE_LIMIT":
      return { ...state, limit: action.payload };
    case "EXISTING_DATA":
      return {
        ...state,
        columns: state.columns.map((x) => ({
          ...x,
          filter:
            action.data.filters.find((y) => y.key === x.key)?.values ||
            x.filter,
        })),
        limit: action.data.limit || state.limit,
      };

    default:
      return state;
  }
}

export default function Product() {
  const [filterData, dispatch] = useReducer(
    filterReducer,
    productFilterInitialState,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const [productData, setProductData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = React.useState(0);
  const [dataCount, setDataCount] = useState(15);
  const [searchValue, setSearchValue] = useState("");
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [clientFilter, setClientFilter] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const clientId = localStorage.getItem("client_id");
  const { user } = useContext(AppContext);
  const roles = localStorage.getItem("roles")
    ? JSON.parse(localStorage.getItem("roles"))
    : [];
  const [showUploadStatus, setShowUploadStatus] = useState(false);
  const [showASNModal, setShowASNModal] = useState(false);
  const [uploadStatusData, setUploadStatusData] = useState([]);
  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;
  const [fetchData, setFetchData] = useState(0);
  const [openImportTypeDialog, setOpenImportTypeDialog] = useState(false);

  const updateFilterData = () => {
    localStorage.setItem(
      "product-filter-data",
      JSON.stringify({
        populate: filterData.populate,
        filters: filterData.columns.map((x) => ({
          key: x.key,
          values: x.filter,
        })),
        limit: filterData.limit,
      }),
    );
  };

  useEffect(() => {
    let data = localStorage.getItem("product-filter-data");
    if (data) {
      localStorage.removeItem("product-filter-data");
      dispatch({
        type: "EXISTING_DATA",
        data: JSON.parse(data),
      });
    }
  }, []);

  const fetchProductData = async (signal) => {
    setLoading(true);
    try {
      const response = await productService.findAll(
        clientFilter ? clientFilter?._id : null,
        searchValue,
        buildQuery(filterData, page),
        { signal },
      );

      setProductData(response.data);
      setDataCount(response.total);
    } catch (error) {
      console.error("Fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    fetchProductData(signal);
    return () => {
      controller.abort();
    };
  }, [filterData, searchValue, page, clientFilter, fetchData]);

  const syncProductData = async () => {
    setLoading(true);
    try {
      const response = await productService.syncProduct({
        client_id: clientId,
        core: true,
      });
      console.log(response);
      toast.success("Product import has been successfully initiated!");
    } catch (error) {
      console.error("Fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    console.log(newFilter);
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };

  return (
    <>
      <DashboardContainer
        searchBarPlaceholder={"Search by Product Name"}
        // onChangeFilter={handleFilterChange}
        filterData={filterData}
        fields={filterData.columns}
        hideUploadButton
        showRefreshButton
        headerButtons={
          <>
            {roles.find(
              (x) =>
                x.name === "Client - Premium" || x.name === "Client - Standard",
            ) ? (
              isMobile() ? (
                <Box sx={{ display: "flex" }}>
                  <IconButton
                    onClick={() => {
                      updateFilterData();
                      setOpenImportTypeDialog(true);
                    }}
                  >
                    <ImportIcon fill={primary_color} />
                  </IconButton>
                  <IconButton onClick={syncProductData} color="primary">
                    <SyncAltIcon fill={primary_color} />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={1}
                  justifyContent="flex-start"
                >
                  <Button
                    size="small"
                    style={{
                      textTransform: "none",
                    }}
                    variant="outlined"
                    color="primary"
                    onClick={syncProductData}
                  >
                    <SyncAltIcon fill={primary_color} />
                    <CustomTypography
                      fontSize="14px"
                      fontWeight={500}
                      color={"primary"}
                    >
                      Sync
                    </CustomTypography>
                  </Button>

                  <Button
                    size="small"
                    style={{
                      textTransform: "none",
                    }}
                    variant="contained"
                    onClick={() => {
                      updateFilterData();
                      setOpenImportTypeDialog(true);
                    }}
                  >
                    <ImportIcon fill="#FFF" />
                    <CustomTypography
                      fontSize="14px"
                      fontWeight={500}
                      color="#FFFFFF"
                    >
                      Import
                    </CustomTypography>
                  </Button>
                </Box>
              )
            ) : null}
          </>
        }
        showUploadStatus
        onSearchData={(x) => {
          // Check if there’s a search term
          if (x.trim()) {
            setSearchValue(x);
            setPage(0); // Reset to page 0 when there’s a search term
          } else {
            setSearchValue(""); // Clear the search value if empty search term
          }
        }}
        onClickAddButton={() => {
          // localStorage.setItem('path', `/manage-product/create`)
          updateFilterData();
          navigate("/manage-product/create");
        }}
        header={"Product List"}
        // onSelectClient={(e) => {
        //   setClientFilter(e);
        // }}
        selectedClient={clientFilter?.name}
        onHandleUploadButton={() => setOpenUploadModal(true)}
        // product
        title={"+ Product"}
        setViewMode={setViewMode}
        onClickRefresh={() => setFetchData(fetchData + 1)}
        onClickSyncProduct={syncProductData}
        onClickCreateASN={() => {
          setShowASNModal(true);
        }}
        hideFilterClient
      >
        <ProductUploadModal
          onClickStatus={() => setShowUploadStatus(true)}
          showUploadStatus
          onSuccessUpload={() => setFetchData(fetchData + 1)}
          product
          onSubmitUploadData={() => setOpenUploadModal(false)}
          onClose={() => setOpenUploadModal(false)}
          open={openUploadModal}
          title={"Product List"}
          selectedData={uploadStatusData}
        ></ProductUploadModal>

        <ProductList
          data={productData}
          onEditData={() => setFetchData(fetchData + 1)}
          onDeleteData={() => setFetchData(fetchData + 1)}
          client={clientId}
          viewMode={viewMode}
          //setFilter={setFilter}
          deleteTitle={"Delete Product"}
          dataCount={dataCount}
          page={page}
          rowsPerPage={filterData.limit}
          setRowsPerPage={(e) =>
            dispatch({
              type: "UPDATE_LIMIT",
              payload: e,
            })
          }
          setPage={setPage}
          onChangeFilter={handleFilterChange}
          filterData={filterData}
          client_id={clientId}
          loading={loading}
          setLoading={setLoading}
        />
        <UploadStatusChartDialog
          onClose={() => setShowUploadStatus(false)}
          open={showUploadStatus}
        ></UploadStatusChartDialog>
      </DashboardContainer>

      <Dialog
        open={openImportTypeDialog}
        onClose={() => setOpenImportTypeDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            minWidth: 480,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            pt: 3,
            pb: 1,
          }}
        >
          <Box>
            <CustomTypography fontSize="18px" fontWeight={700}>
              Select Import Type
            </CustomTypography>
            <CustomTypography
              fontSize="13px"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Choose what you would like to import
            </CustomTypography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setOpenImportTypeDialog(false)}
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ px: 3, py: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {[
              {
                label: "Product",
                description:
                  "Import your product catalogue including SKUs, pricing, and variants.",
                icon: <CategoryIcon sx={{ fontSize: 32 }} />,
                route: "/product-import",
                color: primary_color,
              },
              {
                label: "Supplier Configuration",
                description:
                  "Import supplier config with safety stock, lead times, and warehouse mapping.",
                icon: <SettingsIcon sx={{ fontSize: 32 }} />,
                route: "/supplier-config-import",
                color: primary_color,
              },
            ].map((item) => (
              <Card
                key={item.label}
                variant="outlined"
                sx={{
                  borderRadius: "12px",
                  border: `1.5px solid`,
                  borderColor: "divider",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    borderColor: item.color,
                    boxShadow: `0 0 0 3px ${item.color}22`,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => {
                    setOpenImportTypeDialog(false);
                    navigate(item.route);
                  }}
                  sx={{ p: 0 }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: "16px !important",
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "12px",
                        backgroundColor: `${item.color}18`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: item.color,
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <CustomTypography fontSize="15px" fontWeight={600}>
                        {item.label}
                      </CustomTypography>
                      <CustomTypography
                        fontSize="12px"
                        color="text.secondary"
                        sx={{ mt: 0.25, lineHeight: 1.4 }}
                      >
                        {item.description}
                      </CustomTypography>
                    </Box>
                    <ArrowForwardIcon
                      sx={{
                        color: "text.disabled",
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    />
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
